import { supabase } from '@/lib/supabase';

const BUCKET = 'cocktail-photos';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB (aligné avec file_size_limit du bucket)
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Upload une photo de cocktail. Retourne le chemin (à stocker dans
 * cocktails.photo_path). Génère un nom unique pour éviter les collisions.
 */
export async function uploadCocktailPhoto(file: File): Promise<string> {
  if (file.size > MAX_BYTES) {
    throw new Error(
      `Fichier trop gros (${Math.round(file.size / 1024)} KB). Max ${MAX_BYTES / 1024 / 1024} MB.`
    );
  }
  if (!ALLOWED_MIME.includes(file.type)) {
    throw new Error('Format non supporté. Utilise JPEG, PNG ou WEBP.');
  }

  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg';
  const path = `${crypto.randomUUID()}.${safeExt}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (error) throw error;
  return path;
}

/**
 * Génère une URL signée temporaire (1h) pour afficher une photo.
 * Conservée pour compatibilité avec les pages admin ; depuis la
 * migration 20260510_005 le bucket est public, donc `getCocktailPhotoPublicUrl`
 * est préférable côté public (zéro round-trip).
 */
export async function getCocktailPhotoUrl(
  photoPath: string | null | undefined
): Promise<string | null> {
  if (!photoPath) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(photoPath, 3600);
  if (error) {
    console.warn('Échec génération URL signée photo cocktail :', error);
    return null;
  }
  return data?.signedUrl ?? null;
}

export interface PhotoTransform {
  /** Largeur cible en px. */
  width?: number;
  /** Hauteur cible en px. */
  height?: number;
  /** Qualité 0-100 (défaut 75). */
  quality?: number;
}

/**
 * URL publique permanente (depuis la migration 20260510_005 qui
 * rend le bucket public). Pas de round-trip — synchrone.
 *
 * Accepte un paramètre `transform` pour servir une version resize
 * + compressée via Supabase Storage Image Transformations.
 */
export function getCocktailPhotoPublicUrl(
  photoPath: string | null | undefined,
  transform?: PhotoTransform
): string | null {
  if (!photoPath) return null;
  const options = transform
    ? {
        transform: {
          width: transform.width,
          height: transform.height,
          quality: transform.quality ?? 75,
          resize: 'cover' as const,
        },
      }
    : undefined;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(photoPath, options);
  return data?.publicUrl ?? null;
}

/**
 * Supprime une photo du bucket. Tolérant aux échecs (logue mais ne throw
 * pas) — si la photo a déjà été supprimée ou n'existe plus, on continue.
 */
export async function deleteCocktailPhoto(
  photoPath: string | null | undefined
): Promise<void> {
  if (!photoPath) return;
  const { error } = await supabase.storage.from(BUCKET).remove([photoPath]);
  if (error) {
    console.warn('Échec suppression photo cocktail :', error);
  }
}
